import { Badge } from "@package/subframe/components/Badge";
import {
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherListTodo,
  FeatherThermometer,
  FeatherXCircle,
} from "@subframe/core";
import { format } from "date-fns";
import type { ReactNode } from "react";

import type { IncidentDetailResponseDto } from "@/generated/model";

import {
  buildIncidentTimeline,
  formatIncidentUser,
  groupIncidentTimelineByDay,
  type IncidentTimelineItem,
} from "./incident-timeline";

interface IncidentTimelineProps {
  incident: IncidentDetailResponseDto;
}

export function IncidentTimeline({ incident }: IncidentTimelineProps) {
  const days = groupIncidentTimelineByDay(buildIncidentTimeline(incident));

  if (days.length === 0) {
    return (
      <span className="text-body font-body text-subtext-color">
        No activity recorded in this incident window.
      </span>
    );
  }

  return (
    <div className="flex w-full flex-col gap-5">
      {days.map((day) => (
        <section key={day.key} className="flex w-full flex-col gap-3">
          {/* Prominent day header — the date is stated once, here, instead of
              being repeated faintly on every row. */}
          <div className="flex items-center gap-3">
            <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
              {format(day.date, "EEEE, d MMMM yyyy")}
            </span>
            <div className="h-px grow bg-neutral-100" />
          </div>

          {/* Events for the day. No row gap — the connector rail runs unbroken
              between nodes; each row owns its own bottom padding. */}
          <ol className="flex w-full flex-col">
            {day.items.map((item, index) => (
              <TimelineRow
                key={`${item.kind}-${item.at}-${index}`}
                item={item}
                isLast={index === day.items.length - 1}
              />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function TimelineRow({
  item,
  isLast,
}: {
  item: IncidentTimelineItem;
  isLast: boolean;
}) {
  const v = view(item);
  return (
    <li className="flex w-full gap-3">
      {/* Time gutter — bold, tabular, right-aligned so every time lines up into
          a scannable column down the left edge. */}
      <time
        className="w-11 shrink-0 pt-0.5 text-right text-body-bold font-body-bold tabular-nums text-default-font"
        dateTime={item.at}
      >
        {format(new Date(item.at), "HH:mm")}
      </time>

      {/* Rail — a node dot plus the connector line to the next event. */}
      <div className="flex w-4 shrink-0 flex-col items-center">
        <span
          className={`z-10 mt-1 flex h-4 w-4 items-center justify-center rounded-full border ${v.dot}`}
        >
          {v.icon}
        </span>
        {!isLast && <span className="w-px grow bg-neutral-200" />}
      </div>

      {/* Content */}
      <div
        className={`flex flex-col items-start gap-1 ${isLast ? "" : "pb-5"}`}
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {v.title}
          </span>
          {v.badges}
        </div>
        {v.detail && (
          <span className="text-caption font-caption text-default-font">
            {v.detail}
          </span>
        )}
        {v.note && (
          <span className="text-caption font-caption text-default-font">
            “{v.note}”
          </span>
        )}
        {v.by && (
          <span className="text-caption font-caption text-subtext-color">
            {v.by}
          </span>
        )}
      </div>
    </li>
  );
}

interface RowView {
  /** Tailwind classes for the node dot (border + fill). */
  dot: string;
  icon: ReactNode;
  title: string;
  badges?: ReactNode;
  detail?: string;
  note?: string | null;
  /** Pre-formatted "by <name>" attribution, when there's an actor. */
  by?: string;
}

const iconCls = "h-2.5 w-2.5";

function view(item: IncidentTimelineItem): RowView {
  switch (item.kind) {
    case "reading": {
      const { entry, isTriggering } = item;
      return {
        dot: entry.isOutOfRange
          ? "border-warning-700 bg-warning-100"
          : "border-success-600 bg-success-100",
        icon: (
          <FeatherThermometer
            className={`${iconCls} ${entry.isOutOfRange ? "text-warning-800" : "text-success-700"}`}
          />
        ),
        title: `${entry.temperature}°C`,
        badges: (
          <>
            {entry.isOutOfRange ? (
              <Badge variant="warning">Out of range</Badge>
            ) : (
              <Badge variant="success">In range</Badge>
            )}
            {isTriggering && (
              <Badge variant="neutral">Triggering reading</Badge>
            )}
            {entry.thresholdMinAtLog !== null &&
              entry.thresholdMaxAtLog !== null && (
                <span className="text-caption font-caption text-subtext-color">
                  target {entry.thresholdMinAtLog}°C to{" "}
                  {entry.thresholdMaxAtLog}
                  °C
                </span>
              )}
          </>
        ),
        note: entry.notes,
        by: `Recorded by ${formatIncidentUser(entry.recordedBy)}`,
      };
    }
    case "alert-triggered":
      return {
        dot: "border-warning-800 bg-warning-700",
        icon: <FeatherAlertTriangle className={`${iconCls} text-white`} />,
        title: "Out-of-range alert triggered",
        detail: "Managers notified",
      };
    case "follow-up-created":
      return {
        dot: "border-brand-600 bg-brand-50",
        icon: <FeatherListTodo className={`${iconCls} text-brand-700`} />,
        title: "Follow-up task created",
        detail: item.description,
        by: item.by ? `Created by ${formatIncidentUser(item.by)}` : undefined,
      };
    case "follow-up-completed":
      return {
        dot: "border-success-600 bg-success-100",
        icon: <FeatherCheckCircle className={`${iconCls} text-success-700`} />,
        title: "Follow-up task completed",
        detail: item.description,
        note: item.notes,
        by: item.by ? `Completed by ${formatIncidentUser(item.by)}` : undefined,
      };
    case "follow-up-cancelled":
      return {
        dot: "border-neutral-400 bg-neutral-100",
        icon: <FeatherXCircle className={`${iconCls} text-neutral-600`} />,
        title: "Follow-up task cancelled",
        detail: item.description,
        by: item.by ? `Cancelled by ${formatIncidentUser(item.by)}` : undefined,
      };
    case "signed-off":
      return {
        dot: "border-success-700 bg-success-600",
        icon: <FeatherCheckCircle className={`${iconCls} text-white`} />,
        title: "Incident signed off & closed",
        note: item.notes,
        by: item.by
          ? `Signed off by ${formatIncidentUser(item.by)}`
          : undefined,
      };
  }
}
